import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { Header } from './Dashboard';

const SEGMENTS = [
  { key: 'all', label: 'All users' },
  { key: 'premium', label: 'Premium members' },
  { key: 'free', label: 'Free users' },
  { key: 'verified', label: 'Verified users' },
  { key: 'inactive', label: 'Inactive (7d+)' },
];

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [counts, setCounts] = useState({});
  const [form, setForm] = useState({ subject: '', body: '', segment: 'all' });
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState('');

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([api.get('/admin/campaigns'), api.get('/admin/segments')]);
    setCampaigns(c.campaigns);
    setCounts(s.counts);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async () => {
    if (!form.subject || !form.body) return;
    setSending(true);
    setFlash('');
    try {
      const data = await api.post('/admin/campaigns', form);
      setFlash(`✅ Sent to ${data.campaign.recipients} users`);
      setForm({ subject: '', body: '', segment: 'all' });
      load();
    } catch (e) {
      setFlash(`⚠️ ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const recipientCount = counts[form.segment] ?? '…';

  return (
    <div>
      <Header title="Email Campaigns" sub="Compose and send promotional emails to user segments" />
      <div style={styles.layout}>
        <div className="card" style={{ height: 'fit-content' }}>
          <h3 style={{ marginTop: 0 }}>New campaign</h3>

          <label style={styles.label}>Audience segment</label>
          <select className="input" value={form.segment} onChange={(e) => set('segment', e.target.value)}>
            {SEGMENTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}{counts[s.key] != null ? ` (${counts[s.key]})` : ''}</option>
            ))}
          </select>
          <div style={styles.recipients}>📬 {recipientCount} recipients with an email on file</div>

          <label style={styles.label}>Subject</label>
          <input className="input" value={form.subject} onChange={(e) => set('subject', e.target.value)} placeholder="✨ This weekend on SparkSync…" />

          <label style={styles.label}>Message</label>
          <textarea className="input" rows={7} value={form.body} onChange={(e) => set('body', e.target.value)} placeholder="Write your email…" />

          {flash ? <div style={styles.flash}>{flash}</div> : null}
          <button className="btn" style={{ marginTop: 14, width: '100%' }} onClick={send} disabled={sending}>
            {sending ? 'Sending…' : 'Send Campaign'}
          </button>
          <p style={styles.note}>Email delivery is simulated in this build. Connect an email provider (e.g. SendGrid/SES) on the server.</p>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table>
            <thead>
              <tr><th>Subject</th><th>Segment</th><th>Recipients</th><th>Status</th><th>Sent</th></tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c._id}>
                  <td style={{ color: '#fff' }}>{c.subject}</td>
                  <td style={{ textTransform: 'capitalize' }}>{c.segment}</td>
                  <td>{c.recipients}</td>
                  <td><span className={`badge ${c.status === 'sent' ? 'active' : 'incomplete'}`}>{c.status}</span></td>
                  <td>{c.sentAt ? new Date(c.sentAt).toLocaleString() : '—'}</td>
                </tr>
              ))}
              {campaigns.length === 0 ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30 }}>No campaigns sent yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const styles = {
  layout: { display: 'grid', gridTemplateColumns: '380px 1fr', gap: 20, alignItems: 'start' },
  label: { display: 'block', color: 'var(--text-dim)', fontSize: 13, fontWeight: 600, margin: '14px 0 6px' },
  recipients: { color: 'var(--text-muted)', fontSize: 13, marginTop: 8 },
  flash: { marginTop: 14, fontSize: 14, fontWeight: 600, color: 'var(--text-dim)' },
  note: { color: 'var(--text-muted)', fontSize: 12, marginTop: 12 },
};
